<template>
  <!--
    O QUE ESTE MODAL BLOQUEIA, E O QUE ELE NAO BLOQUEIA.

    Ele nao fecha sem aceite: nao ha botao de fechar, nao ha ESC, e clicar fora
    nao dispensa. Mas ele ocupa a AREA DA PAGINA, e nao a janela inteira — o
    menu lateral e o cabecalho do portal continuam alcancaveis.

    A diferenca importa. Com `position: fixed; inset: 0`, quem entrasse aqui e
    nao quisesse aceitar ficaria preso no portal INTEIRO, sem conseguir voltar
    ao Dashboard nem sair. Isso nao e um termo bloqueante, e um sequestro. O que
    se quer e que o ASSISTENTE nao seja usado sem aceite — e para isso basta que
    o quadro nao exista enquanto o pedido estiver de pe, o que quem chama garante.

    E UM CARTAO, E NAO UM VEU. Comecou como overlay escuro sobre a pagina, do
    jeito que modal costuma ser — mas veu existe para escurecer o que ele
    bloqueia, e aqui nao ha nada atras: o quadro do assistente nem foi montado.
    O resultado na tela era um retangulo cinza grande cobrindo o vazio, que le
    como area quebrada, nao como bloqueio.

    Agora e o `.card` do proprio portal, ocupando o lugar exato onde o
    assistente vai aparecer depois do aceite. E honesto: e ali que a coisa vai
    estar, e ainda nao esta.
  -->
  <div class="card terms-area">
    <div
      class="terms-stage"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-title"
      aria-describedby="terms-text">
      <div class="terms-modal">
        <div class="terms-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true">
            <path d="M10 2.5 11.75 7.75 17 9.5 11.75 11.25 10 16.5 8.25 11.25 3 9.5 8.25 7.75Z" />
            <path d="M17.5 13 18.4 15.6 21 16.5 18.4 17.4 17.5 20 16.6 17.4 14 16.5 16.6 15.6Z" />
          </svg>
        </div>

        <h3 id="terms-title" class="terms-title">
          Antes de usar o seu novo assistente de vendas
        </h3>

        <p id="terms-text" class="terms-text">
          Você precisa confirmar que leu e concorda com
          <span v-for="(doc, i) in documents" :key="doc.document"><a
            :href="doc.url"
            target="_blank"
            rel="noopener noreferrer">{{ doc.nome }}</a>{{ separator(i) }}</span>
        </p>

        <!--
          O erro tem de aparecer AQUI, e nao virar um modal que nao responde.
          Este e o unico caminho de saida da tela, entao um clique que falha em
          silencio deixa a pessoa presa sem saber por que.
        -->
        <p v-if="hasError" class="terms-error" role="alert">
          Não foi possível registrar seu aceite agora. Tente de novo em instantes —
          se o erro continuar, avise o suporte.
        </p>

        <button
          ref="botao"
          type="button"
          class="terms-button"
          :disabled="submitting"
          @click="accept">
          {{ submitting ? 'Registrando...' : 'Li e concordo' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Pedido de aceite dos termos, dentro da pagina do IA4COM (TEL-2272).
 *
 * O componente nao decide SE deve aparecer — quem decide e a pagina, que ja
 * sabe do rollout e consultou as pendencias. Aqui so existe a tela e o clique.
 *
 * O aceite e gravado no servidor, amarrado a pessoa. Nada de `localStorage`:
 * um registro de consentimento que mora no navegador some quando a pessoa
 * troca de maquina, some quando limpa os dados, e nao pode ser respondido
 * depois, fora desta tela — que sao exatamente as tres coisas que ele precisa.
 */
import { acceptTerms, documentLabel } from '../../services/terms'

export default {
  name: 'TermsAcceptanceModal',

  props: {
    /** o que falta aceitar, como o backend devolveu: [{ document, version }] */
    pendingTerms: { type: Array, required: true }
  },

  data () {
    return {
      submitting: false,
      hasError: false
    }
  },

  computed: {
    /** os documentos pendentes com nome e link legiveis, na ordem que vieram */
    documents () {
      return this.pendingTerms.map(p => {
        const rotulo = documentLabel(p.document)
        return { document: p.document, nome: rotulo.nome, url: rotulo.url }
      })
    }
  },

  mounted () {
    // o unico controle da tela recebe o foco: quem navega por teclado nao
    // precisa procurar o que fazer numa tela que nao deixa fazer mais nada
    if (this.$refs.botao) this.$refs.botao.focus()
  },

  methods: {
    /** ", " entre os documentos, " e " antes do ultimo, "." no fim */
    separator (i) {
      if (i === this.documents.length - 1) return '.'
      if (i === this.documents.length - 2) return ' e '
      return ', '
    },

    accept () {
      if (this.submitting) return
      this.submitting = true
      this.hasError = false

      acceptTerms()
        .then(pendingTermsRestantes => {
          /*
           * Só libera quando o servidor confirma que NAO SOBROU pendencia.
           * Fechar no `then` sem olhar a resposta deixaria a tela passar por
           * um aceite que gravou pela metade — e a proxima visita levantaria o
           * modal de novo, sem explicacao nenhuma para quem acabou de aceitar.
           */
          if (pendingTermsRestantes.length) {
            this.hasError = true
            this.submitting = false
            return
          }
          this.$emit('aceito')
        })
        .catch(error => {
          console.error('[terms] falha ao registrar aceite', error)
          this.hasError = true
          this.submitting = false
        })
    }
  }
}
</script>

<style scoped>
/*
 * O CARTAO OCUPA O LUGAR DO ASSISTENTE.
 *
 * `.card` e a classe do proprio portal (assets/scss/_cards.scss): o branco, o
 * raio e a sombra saem do tema, entao esta tela acompanha se o tema mudar. E o
 * mesmo `.card` que envolve o quadro depois do aceite — de proposito, para que
 * a moldura nao pule de forma quando o assistente entra.
 *
 * `margin-bottom: 0` e `transition: none` pelo mesmo motivo do `.ia4com-card`
 * do Index.vue: o `.card` do tema nasceu para empilhar com outros e traz 30px
 * embaixo e meio segundo de transicao em todas as propriedades.
 */
.terms-area {
  margin-bottom: 0;
  transition: none;
  min-height: calc(100vh - 220px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

/* so centraliza; quem desenha a caixa e o `.card` em volta */
.terms-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.terms-modal {
  max-width: 460px;
  width: 100%;
  text-align: center;
}

/* o mesmo ciano da marca usado no cabecalho da pagina e no item do menu */
.terms-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  border-radius: 12px;
  background: #36bbef;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.terms-icon svg {
  width: 26px;
  height: 26px;
  display: block;
}

.terms-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 10px;
}

.terms-text {
  font-size: 0.9rem;
  line-height: 1.55;
  color: #4b5563;
  margin: 0 0 20px;
}
.terms-text a {
  color: #007bff;
  text-decoration: underline;
}

.terms-error {
  font-size: 0.85rem;
  line-height: 1.5;
  color: #b3261e;
  background: #fdecea;
  border-radius: 6px;
  padding: 10px 12px;
  margin: 0 0 16px;
  text-align: left;
}

/*
 * A LARGURA SAI DO TEXTO, E NAO DO CARTAO.
 *
 * Era `width: 100%` — uma barra azul de 460px de ponta a ponta para uma acao
 * de tres palavras. Botao daquele tamanho le como faixa de destaque, nao como
 * coisa em que se clica, e o peso dele competia com o proprio aviso.
 *
 * `min-width` segura o outro extremo: encolhido ate o texto, viraria um alvo
 * pequeno demais para o dedo. Ele se centraliza sozinho — `.terms-modal` e
 * `text-align: center` e o botao e inline-block por padrao.
 */
.terms-button {
  min-width: 180px;
  border: 0;
  border-radius: 6px;
  background: #007bff;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 11px 28px;
  cursor: pointer;
}
.terms-button:hover:not(:disabled) {
  background: #0069d9;
}
.terms-button:disabled {
  opacity: 0.65;
  cursor: default;
}
.terms-button:focus-visible {
  outline: 2px solid #1f2937;
  outline-offset: 2px;
}
</style>



// WEBPACK FOOTER //
// src/components/IA4COM/TermsAcceptanceModal.vue